// The overworld's camera (docs/42 on the World screen): the chart zooms and pans like the encounter
// board — wheel / trackpad pinch / Safari gestures / two fingers, Q out and E in, WASD and arrows (the
// camera hotkeys of `core/input/hotkeys.ts`),
// click-drag with a flick, and the mouse resting at the window's edge. The whole stage (canvas,
// roads, nodes, bands, the land sky) is one element under a CSS `translate() scale()` with origin
// 0 0, so everything on it stays aligned and the buttons hit-test through the transform for free.
// Pure geometry here (tested); the listeners and the ease live in `useOverworldCamera`.

/** The camera: where the stage's top-left sits in the map element (CSS px) and its scale (1 = the chart's own 48 px cells). */
export interface OwCam { x: number; y: number; z: number }
export interface Size { w: number; h: number }

/** docs/42 CM1 on the chart: the furthest in (× the chart's own 48 px cells). */
export const OW_ZOOM_MAX = 3;
/** docs/42 CM5: the edge band (CSS px from the window's edge) and what an edge pan never starts over (the encounter's list). */
export const OW_EDGE_BAND = 12;
export const OW_EDGE_BLOCKERS = 'button, a, input, select, textarea, label, summary, [role="button"], [role="slider"], [role="tab"], [role="menu"], [role="menuitem"], [role="dialog"], [role="listbox"], .fk-hit';
/** docs/42 CM4: easing time constants (s) — zoom, pinch, held-key and edge pans, drags. */
export const OW_EASE = { zoom: 0.1, pinch: 0.045, pan: 0.07, drag: 0.035 } as const;
/** docs/33 E1: how far (CSS px) a press travels before it is a drag and no longer a click. */
export const OW_DRAG_SLOP = 6;

/** The furthest out: the whole chart fits the view, never below the chart's own size where it already fits. */
export function owMinZoom(map: Size, view: Size): number {
  if (map.w <= 0 || map.h <= 0 || view.w <= 0 || view.h <= 0) return 1;
  return Math.min(1, view.w / map.w, view.h / map.h);
}

/** A zoom kept between the fit and `OW_ZOOM_MAX`. */
export function clampOwZoom(z: number, min: number): number { return Math.min(OW_ZOOM_MAX, Math.max(min, z)); }

/**
 * Keep the chart on screen: a chart wider than the view covers it but for a little slack (so the
 * default framing, which leaves a strip under the quest column on wide windows, is reachable); a
 * narrower one stays inside, give or take the same slack. Slack is a fifth of the smaller of the two.
 */
export function clampOwCam(cam: OwCam, map: Size, view: Size): OwCam {
  const axis = (v: number, size: number, span: number) => {
    const s = size * cam.z, slack = Math.min(span, s) / 5;
    const lo = Math.min(0, span - s) - slack, hi = Math.max(0, span - s) + slack;
    return Math.max(lo, Math.min(hi, v));
  };
  return { x: axis(cam.x, map.w, view.w), y: axis(cam.y, map.h, view.h), z: cam.z };
}

/** docs/121 VW7: a locked stage zooms out no further than its whole width across the view. */
export function lockedMinZoom(map: Size, view: Size): number {
  if (map.w <= 0 || view.w <= 0) return 1;
  return Math.min(OW_ZOOM_MAX, view.w / map.w);
}

/**
 * docs/121 VW7: a locked stage (the village street) never shows past its sides or below its foot: it
 * covers the view's width, and stands on the view's bottom unless zoomed in far enough to see over it,
 * when it may be panned up to `above` stage px over its top (the far hills painted there) and no more.
 */
export function clampLockedCam(cam: OwCam, map: Size, view: Size, above = 0): OwCam {
  const z = Math.max(cam.z, lockedMinZoom(map, view));
  const xlo = view.w - map.w * z, x = xlo >= 0 ? xlo / 2 : Math.max(xlo, Math.min(0, cam.x));
  const ylo = view.h - map.h * z, yhi = Math.max(ylo, above * z);
  return { x, y: Math.max(ylo, Math.min(yhi, cam.y)), z };
}

/** The camera at zoom `z` with the chart point under `at` (map-element CSS px) kept where it is. */
export function zoomOwAbout(cam: OwCam, z: number, at: { x: number; y: number }): OwCam {
  const px = (at.x - cam.x) / cam.z, py = (at.y - cam.y) / cam.z;
  return { x: at.x - px * z, y: at.y - py * z, z };
}

/** The chart point (stage CSS px at scale 1) under a map-element point. */
export function owToStage(cam: OwCam, at: { x: number; y: number }): { x: number; y: number } {
  return { x: (at.x - cam.x) / cam.z, y: (at.y - cam.y) / cam.z };
}

/**
 * The stage's CSS transform for `cam`, given where its CSS layout already put it (`base`). Whole
 * device pixels at rest; in motion the offset stays exact, since snapping it under a changing scale
 * shakes the anchored point by up to half a pixel a frame.
 */
export function owTransform(cam: OwCam, base: { x: number; y: number }, dpr = 1, rest = true): string {
  const r = (v: number) => (rest ? Math.round(v * dpr) / dpr : v);
  return `translate(${r(cam.x - base.x)}px, ${r(cam.y - base.y)}px) scale(${cam.z})`;
}

/** docs/42 CM4: a released flick coasts on for about a tenth of a second of its last speed (CSS px; none for a slow drag). */
export function flingOffset(trail: ReadonlyArray<{ t: number; x: number; y: number }>, upAt: number): [number, number] {
  const a = trail[0], b = trail[trail.length - 1];
  if (!a || !b || b.t - a.t < 8 || upAt - b.t > 60) return [0, 0];
  const coast = 0.09, max = 260;
  let dx = ((b.x - a.x) / (b.t - a.t)) * 1000 * coast, dy = ((b.y - a.y) / (b.t - a.t)) * 1000 * coast;
  const n = Math.hypot(dx, dy);
  if (n < 4) return [0, 0];
  if (n > max) { dx *= max / n; dy *= max / n; }
  return [dx, dy];
}

/** Keys typed into a field (the dock's chat) never move the chart. */
export function typingIn(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && typeof el.tagName === 'string' && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}

/**
 * One frame of the ease toward `goal` (docs/42 CM4): the zoom on its own constant; with an anchor
 * the anchored chart point stays under the pointer through the whole ease, else the pan follows on
 * `k`. Snaps once within a hair. `kz`, `k` are 0..1 (1 = jump, as under reduce motion).
 */
export function easeOwCam(cur: OwCam, goal: OwCam, anchor: { sx: number; sy: number; px: number; py: number } | undefined, kz: number, k: number): { cam: OwCam; anchorDone: boolean } {
  const near = (a: number, b: number, eps: number) => Math.abs(a - b) < eps;
  const z = near(cur.z, goal.z, 0.001) ? goal.z : cur.z + (goal.z - cur.z) * kz;
  if (anchor) {
    if (z === goal.z) return { cam: { ...goal }, anchorDone: true };
    return { cam: { x: anchor.sx - anchor.px * z, y: anchor.sy - anchor.py * z, z }, anchorDone: false };
  }
  const x = near(cur.x, goal.x, 0.25) ? goal.x : cur.x + (goal.x - cur.x) * k;
  const y = near(cur.y, goal.y, 0.25) ? goal.y : cur.y + (goal.y - cur.y) * k;
  return { cam: { x, y, z }, anchorDone: false };
}
